import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Avatar,
    CardActionArea,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
    CheckCircleOutline as CheckCircleIcon,
    Person as PersonIcon,
    Home as HomeIcon,
    DirectionsCar as CarIcon,
    Insights as InsightsIcon,      // For Why Choose Us
    SupportAgent as SupportAgentIcon, // For Why Choose Us
    Newspaper as NewspaperIcon,
    BarChart as BarChartIcon,
    Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import SEO from '../components/SEO/SEO';
import { getSeoData } from '../utils/seoConfig';
import { getAppTheme } from '../services/authContext';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';



// --- CONFIGURATION & MOCK DATA ---
const FD_CTA_LINK = "/lamf";
const CALCULATOR_CTA_LINK = "/calc";
const NEWS_PAGE_LINK = "/news";

const heroData = {
    title: "Get Instant Cash with Loan Against Mutual Funds",
    subtitle: "Unlock the value of your MF portfolio without selling your investments.",
    features: [
        "Instant approval with minimal documentation",
        "Continue earning returns on your mutual funds"
    ],
    image: "/img_frontend/mle/mleh2.jpg"  // Replace if you have a specific MF-based banner
};


const whyChooseUsData = {
    title: "Your Trusted Partner in Financial Growth",
    subtitle: "We are committed to providing you with the tools and expertise you need to navigate your financial journey with confidence.",
    benefits: [
        {
            title: "Data-Driven Insights",
            description: "Leverage our powerful analytics and comprehensive data to make smarter investment and loan decisions. We simplify complex financial information so you can see a clear path to your goals.",
            icon: <InsightsIcon />,
               image: "/img_frontend/mle/mleh3.jpg"
        },
        {
            title: "Expert Support, Always",
            description: "Our team of financial advisors is here to help you every step of the way. From planning your first investment to managing your loan portfolio, we provide personalized guidance tailored to your needs.",
            icon: <SupportAgentIcon />,
              image: "/img_frontend/mle/mleh4.jpg"
        }
    ]
};

const calculatorData = {
    title: "Plan Your Finances with Precision",
    subtitle: "Knowledge is the first step towards financial freedom. Our calculators are designed to give you a clear picture of your financial commitments and investment potential, empowering you to make confident decisions.",
    calculators: [
        { name: "Personal Loan EMI", icon: <PersonIcon />, path: "/calc" },
        { name: "Home Loan EMI", icon: <HomeIcon />, path: "/calc" },
        { name: "Car Loan EMI", icon: <CarIcon />, path: "/calc" },
    ]
};

// --- REUSABLE COMPONENTS ---
const Section = ({ children, sx = {}, ...props }) => (
    <Box component="section" sx={{ py: { xs: 6, md: 8 }, ...sx }} {...props}>
        <Container maxWidth="lg">{children}</Container>
    </Box>
);

const SectionTitle = ({ title, subtitle, ...props }) => (
    <Box sx={{ textAlign: 'center', mb: 6 }} {...props}>
        <Typography variant="h3" component="h2" gutterBottom>{title}</Typography>
        {subtitle && <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '750px', mx: 'auto' }}>{subtitle}</Typography>}
    </Box>
);

// --- HOME PAGE SECTIONS ---

const HeroSection = ({ data }) => (
    <Section sx={{ backgroundColor: 'primary.main', color: 'white' }}>
        <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
                <Typography variant="h2" component="h1" gutterBottom>{data.title}</Typography>
                <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>{data.subtitle}</Typography>
                {data.features.map((feature, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <CheckCircleIcon sx={{ mr: 1.5, color: '#a5d6a7' }} />
                        <Typography>{feature}</Typography>
                    </Box>
                ))}
                <Button variant="contained" size="large" component={Link} to={FD_CTA_LINK} endIcon={<ArrowForwardIcon />}
                    sx={{ mt: 4, py: 1.5, px: 4, backgroundColor: 'white', color: 'primary.main', '&:hover': { backgroundColor: 'grey.100' } }}>
                    Explore Loan Against Mutual Fund
                </Button>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                <img src={data.image} alt="Savings Illustration" style={{ maxWidth: '100%', height: 'auto',borderRadius:"50px" }} />
            </Grid>
        </Grid>
    </Section>
);

// New "Why Choose Us" Section with alternating layout
const WhyChooseUsSection = ({ data }) => (
    <Section>
        <SectionTitle title={data.title} subtitle={data.subtitle} />
        {data.benefits.map((benefit, index) => (
            <Grid container spacing={6} alignItems="center" key={benefit.title} sx={{ mb: index < data.benefits.length - 1 ? 8 : 0 }} direction={index % 2 === 0 ? 'row' : 'row-reverse'}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ width: '100%', height: { xs: 280, md: 400 }, borderRadius: 2, overflow: 'hidden', boxShadow: 5 }}>
                        <img src={benefit.image} alt={benefit.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', color: 'white', mr: 2 }}>{benefit.icon}</Avatar>
                        <Typography variant="h4" component="h3">{benefit.title}</Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">{benefit.description}</Typography>
                </Grid>
            </Grid>
        ))}
    </Section>
);


const CalculatorsSection = ({ data }) => (
    <Section sx={{ backgroundColor: 'background.paper' }}>
        <SectionTitle title={data.title} subtitle={data.subtitle} />
        <Grid container spacing={4} justifyContent="center">
            {data.calculators.map((calc) => (
                <Grid item xs={12} sm={6} md={4} key={calc.name}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 } }}>
                        <CardActionArea component={Link} to={calc.path} sx={{ flexGrow: 1 }}>
                            <CardContent sx={{ textAlign: 'center', p: 4 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
                                    {React.cloneElement(calc.icon, { sx: { fontSize: 32 } })}
                                </Avatar>
                                <Typography variant="h5" gutterBottom>{calc.name}</Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            ))}
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button variant="outlined" size="large" component={Link} to={CALCULATOR_CTA_LINK} endIcon={<ArrowForwardIcon />}>
                View All Calculators
            </Button>
        </Box>
    </Section>
);

// Revamped "Stay Updated" Section
const StayUpdatedSection = () => (
    <Section>
        <SectionTitle title="Stay Informed with Financial News" subtitle="Knowledge is power. Get the latest market updates and investment insights to make smarter financial decisions." />
        <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                    <List>
                        <ListItem>
                            <ListItemIcon><BarChartIcon color="primary" fontSize="large" /></ListItemIcon>
                            <ListItemText primary="Market Analysis" secondary="In-depth analysis of market trends and economic indicators." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><LightbulbIcon color="primary" fontSize="large" /></ListItemIcon>
                            <ListItemText primary="Expert Investment Tips" secondary="Actionable advice from financial experts to grow your wealth." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><NewspaperIcon color="primary" fontSize="large" /></ListItemIcon>
                            <ListItemText primary="Latest Financial News" secondary="Stay ahead with the most recent news affecting your investments." />
                        </ListItem>
                    </List>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom>Don't Miss an Update</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>Visit our news section to explore articles, reports, and more.</Typography>
                <Button variant="contained" size="large" component={Link} to={NEWS_PAGE_LINK} endIcon={<ArrowForwardIcon />}>
                    Read Our News
                </Button>
            </Grid>
        </Grid>
    </Section>
);


// --- MAIN HOME PAGE COMPONENT ---
const HomePage = () => {
    const mode = useSelector((state) => state.theme.mode);
    const theme = getAppTheme(mode);
    const seoData = getSeoData("/");
    
    return (
        <ThemeProvider theme={theme}>
            <SEO {...seoData} />
            <Box sx={{ backgroundColor: 'background.default' }}>
                <HeroSection data={heroData} />
                <WhyChooseUsSection data={whyChooseUsData} />
                <CalculatorsSection data={calculatorData} />
                <StayUpdatedSection />
            </Box>
        </ThemeProvider>
    );
};

export default HomePage;
